import React, { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Toaster } from '../components/ui/sonner';
import './studio.css';
import { StudioI18nProvider } from './studioI18n';
import { StudioAuthProvider, useStudioAuth } from './StudioAuthContext';
import { ProjectProvider } from './ProjectContext';
import { StudioHeader } from './components/StudioHeader';
import LandingPage from './pages/LandingPage';
import AuthPage, { ResetPage } from './pages/AuthPage';
import ProjectPage, { StepPage } from './pages/ProjectPage';

const Splash = () => <div className="min-h-[100dvh] flex items-center justify-center"><div className="relative h-14 w-14 rounded-full bg-teal-600/80 shadow-lg st-ring" data-testid="studio-splash" /></div>;

const Private = ({ children }) => {
  const { customer } = useStudioAuth();
  if (customer === null) return <Splash />;
  if (customer === false) return <Navigate to="/auth" replace />;
  return <ProjectProvider>{children}</ProjectProvider>;
};

function Shell() {
  const location = useLocation();
  const { customer } = useStudioAuth();
  if (customer === null) return <Splash />;
  return (
    <div className="studio-grain min-h-[100dvh]">
      <StudioHeader />
      <div className="relative z-[1]">
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname.split('/').slice(0, 2).join('/')}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth" element={customer ? <Navigate to="/project" replace /> : <AuthPage />} />
            <Route path="/reset" element={<ResetPage />} />
            <Route path="/project" element={<Private><ProjectPage /></Private>} />
            <Route path="/project/:step" element={<Private><StepPage /></Private>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AnimatePresence>
      </div>
    </div>
  );
}

const STUDIO_ICONS = [['icon', '/studio-icon-32.png', '32x32'], ['icon', '/studio-icon-192.png', '192x192'], ['apple-touch-icon', '/studio-apple-icon-180.png', '180x180']];

export default function StudioApp() {
  useEffect(() => {
    document.documentElement.classList.add('studio-theme');
    document.title = 'PM Studio · Veja sua casa renovada';
    const base = process.env.PUBLIC_URL || '';
    const old = [...document.querySelectorAll('link[rel~="icon"], link[rel="apple-touch-icon"]')];
    old.forEach((l) => l.remove());
    const added = STUDIO_ICONS.map(([rel, href, sizes]) => {
      const l = document.createElement('link');
      l.rel = rel; l.href = `${base}${href}`; l.sizes = sizes; if (rel === 'icon') l.type = 'image/png';
      l.dataset.studioIcon = '1';
      document.head.appendChild(l);
      return l;
    });
    return () => { document.documentElement.classList.remove('studio-theme'); added.forEach((l) => l.remove()); old.forEach((l) => document.head.appendChild(l)); };
  }, []);
  return (
    <StudioI18nProvider>
      <BrowserRouter basename="/studio">
        <StudioAuthProvider>
          <Shell />
          <Toaster richColors position="top-center" closeButton duration={3500} expand visibleToasts={3} />
        </StudioAuthProvider>
      </BrowserRouter>
    </StudioI18nProvider>
  );
}
